import { useState, useEffect } from "react";
import { 
  Loader2, Plus, RefreshCw, Trash2,
  CheckCircle, XCircle, MessageCircle, Phone, Send,
  FileText, ExternalLink, Settings2, UserPlus, RotateCcw, 
  Globe, Key
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WhatsAppIntegration({ currentUser, onRefresh, hideConnect = false }) {
  const [loading, setLoading] = useState(true);
  const [waIntegrations, setWaIntegrations] = useState([]);
  
  // Dialog states
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [logs, setLogs] = useState([]);
  const [formLoading, setFormLoading] = useState(false);

  // API Integration Form
  const [apiForm, setApiForm] = useState({
    businessAccountId: "",
    phoneNumberId: "",
    accessToken: "",
    webhookVerifyToken: "",
    appId: "",
    appSecret: "",
    displayName: "",
    phoneNumber: ""
  });
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  // Auto Reply Settings
  const [autoReplySettings, setAutoReplySettings] = useState({
    enabled: false,
    instantReply: "Thank you for contacting us! We'll get back to you shortly.",
    businessHoursReply: "",
    afterHoursReply: "We're currently closed. Our team will respond during business hours.",
    businessHoursStart: "09:00",
    businessHoursEnd: "18:00"
  });

  // Assignment Rules
  const [assignmentRule, setAssignmentRule] = useState("round_robin");
  const [assignToUser, setAssignToUser] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    fetchData();
    fetchTeamMembers();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const intRes = await fetch(`${BACKEND_URL}/api/whatsapp/integrations`, { credentials: "include" });
      if (intRes.ok) {
        const data = await intRes.json();
        setWaIntegrations(data.integrations || []);
      }
    } catch (e) {
      console.error("Error fetching WhatsApp data:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/users`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setTeamMembers(data.users || data || []);
      }
    } catch (e) {
      console.error("Error fetching team members:", e);
    }
  };

  // API Token Validation
  const handleValidateApiToken = async () => {
    if (!apiForm.accessToken.trim() || !apiForm.phoneNumberId.trim()) {
      toast.error("Please enter both Access Token and Phone Number ID");
      return;
    }

    setValidating(true);
    setValidationResult(null);

    try {
      const response = await fetch(`https://graph.facebook.com/v18.0/${apiForm.phoneNumberId}`, {
        headers: { "Authorization": `Bearer ${apiForm.accessToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        setValidationResult({
          valid: true,
          data: {
            verified_name: data.verified_name,
            display_phone_number: data.display_phone_number,
            quality_rating: data.quality_rating
          }
        });
        setApiForm(prev => ({
          ...prev,
          displayName: data.verified_name || "WhatsApp Business",
          phoneNumber: data.display_phone_number || ""
        }));
        toast.success("Token validated successfully!");
      } else {
        const error = await response.json();
        setValidationResult({
          valid: false,
          error: error.error?.message || "Invalid token or Phone Number ID"
        });
        toast.error("Validation failed");
      }
    } catch (e) {
      setValidationResult({ valid: false, error: "Connection failed" });
      toast.error("Failed to validate token");
    } finally {
      setValidating(false);
    }
  };

  // Connect API Integration
  const handleConnectApi = async () => {
    if (!apiForm.accessToken || !apiForm.phoneNumberId || !apiForm.displayName) {
      toast.error("Please fill all required fields and validate the token");
      return;
    }

    setFormLoading(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/connect-api`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          connection_type: "api",
          access_token: apiForm.accessToken,
          phone_number_id: apiForm.phoneNumberId,
          business_account_id: apiForm.businessAccountId,
          webhook_verify_token: apiForm.webhookVerifyToken || `wa_verify_${Date.now()}`,
          app_id: apiForm.appId,
          app_secret: apiForm.appSecret,
          display_name: apiForm.displayName,
          phone_number: apiForm.phoneNumber,
          assignment_rule: assignmentRule,
          assign_to_user: assignToUser,
          auto_reply_settings: autoReplySettings
        })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        toast.success("WhatsApp Business API connected successfully!");
        resetApiForm();
        setShowConnectDialog(false);
        fetchData();
        if (onRefresh) onRefresh();
      } else {
        toast.error(data.detail || "Failed to connect");
      }
    } catch (e) {
      toast.error("Connection failed");
    } finally {
      setFormLoading(false);
    }
  };

  const resetApiForm = () => {
    setApiForm({
      businessAccountId: "",
      phoneNumberId: "",
      accessToken: "",
      webhookVerifyToken: "",
      appId: "",
      appSecret: "",
      displayName: "",
      phoneNumber: ""
    });
    setValidationResult(null);
  };

  const handleToggle = async (integrationId, field, value) => {
    try {
      await fetch(`${BACKEND_URL}/api/whatsapp/integrations/${integrationId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ [field]: value })
      });
      fetchData();
      toast.success(`${field.replace(/_/g, " ")} ${value ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (integrationId) => {
    if (!confirm("Delete this WhatsApp integration?")) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/integrations/${integrationId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (res.ok) {
        toast.success("Integration deleted");
        fetchData();
        if (onRefresh) onRefresh();
      } else {
        toast.error("Failed to delete");
      }
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const handleReconnect = async (integrationId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/integrations/${integrationId}/reconnect`, {
        method: "POST",
        credentials: "include"
      });

      if (res.ok) {
        toast.success("Reconnection initiated");
        fetchData();
      } else {
        toast.error("Reconnection failed");
      }
    } catch (e) {
      toast.error("Reconnection failed");
    }
  };

  const handleViewLogs = async (integration) => {
    setSelectedIntegration(integration);
    try {
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/integrations/${integration.integration_id}/logs`, {
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setShowLogsDialog(true);
      }
    } catch (e) {
      toast.error("Failed to load logs");
    }
  };

  const handleOpenSettings = (integration) => {
    setSelectedIntegration(integration);
    setAssignmentRule(integration.assignment_rule || "round_robin");
    setAssignToUser(integration.assign_to_user || "");
    setAutoReplySettings(integration.auto_reply_settings || {
      enabled: false,
      instantReply: "",
      businessHoursReply: "",
      afterHoursReply: "",
      businessHoursStart: "09:00",
      businessHoursEnd: "18:00"
    });
    setShowSettingsDialog(true);
  };

  const handleSaveSettings = async () => {
    if (!selectedIntegration) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/whatsapp/integrations/${selectedIntegration.integration_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          assignment_rule: assignmentRule,
          assign_to_user: assignToUser,
          auto_reply_settings: autoReplySettings
        })
      });

      if (res.ok) {
        toast.success("Settings saved");
        setShowSettingsDialog(false);
        fetchData();
      } else {
        toast.error("Failed to save settings");
      }
    } catch (e) {
      toast.error("Failed to save settings");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <>
      {/* WhatsApp Business API Card — hidden when the page provides its own
          connect flow (WhatsApp module → Settings uses Embedded Signup). */}
      {!hideConnect && (
      <Card className="border-2 hover:border-green-300 transition-colors cursor-pointer mb-6" onClick={() => setShowConnectDialog(true)}>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg">WhatsApp Business API</CardTitle>
              <CardDescription>Official Meta Integration</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-4">
            Connect using the official WhatsApp Business API for enterprise-grade automation, 
            template messages, and multi-agent support.
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle className="w-4 h-4" /> Webhook-based real-time sync
            </div>
            <div className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle className="w-4 h-4" /> Template message support
            </div>
            <div className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle className="w-4 h-4" /> Multi-agent support
            </div>
            <div className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle className="w-4 h-4" /> High-volume automation
            </div>
          </div>
          <Button className="w-full mt-4 bg-green-600 hover:bg-green-700" data-testid="connect-api-btn">
            <Key className="w-4 h-4 mr-2" />
            Connect via API
          </Button>
        </CardContent>
      </Card>
      )}

      {/* Connected Integrations */}
      {waIntegrations.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div className="flex gap-3 items-center">
                <div className="p-2 bg-green-600 rounded-lg">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle>Connected Accounts</CardTitle>
                  <CardDescription>{waIntegrations.length} WhatsApp account(s) connected</CardDescription>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {waIntegrations.map(integration => (
                <div 
                  key={integration.integration_id}
                  className="p-4 bg-slate-50 rounded-lg border border-slate-200"
                  data-testid={`wa-integration-${integration.integration_id}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-100">
                        <Globe className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{integration.display_name}</p>
                          <Badge variant="outline" className="text-xs">API</Badge>
                        </div>
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {integration.phone_number}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={
                        integration.status === "connected" 
                          ? integration.enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"
                          : "bg-red-100 text-red-700"
                      }>
                        {integration.status === "connected" ? (integration.enabled ? "Active" : "Paused") : "Disconnected"}
                      </Badge>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                    <div className="p-3 bg-white rounded-lg border">
                      <p className="text-xs text-slate-500">Messages</p>
                      <p className="text-lg font-bold text-green-600">{integration.total_messages_received || 0}</p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border">
                      <p className="text-xs text-slate-500">Leads Created</p>
                      <p className="text-lg font-bold text-blue-600">{integration.total_leads_created || 0}</p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border">
                      <p className="text-xs text-slate-500">Last Message</p>
                      <p className="text-sm font-medium">
                        {integration.last_message_at ? new Date(integration.last_message_at).toLocaleDateString() : "Never"}
                      </p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border">
                      <p className="text-xs text-slate-500">Last Sync</p>
                      <p className="text-sm font-medium">
                        {integration.last_sync_at ? new Date(integration.last_sync_at).toLocaleTimeString() : "Never"}
                      </p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border">
                      <p className="text-xs text-slate-500">Assignment</p>
                      <p className="text-sm font-medium capitalize">{(integration.assignment_rule || "round_robin").replace(/_/g, " ")}</p>
                    </div>
                  </div>

                  {/* Toggle Controls */}
                  <div className="flex flex-wrap gap-4 mb-3">
                    <label className="flex items-center gap-2 text-sm">
                      <span>Enabled</span>
                      <Switch 
                        checked={integration.enabled} 
                        onCheckedChange={v => handleToggle(integration.integration_id, "enabled", v)} 
                      />
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <UserPlus className="w-4 h-4" />
                      <span>Auto-Create Lead</span>
                      <Switch 
                        checked={integration.auto_create_lead} 
                        onCheckedChange={v => handleToggle(integration.integration_id, "auto_create_lead", v)} 
                      />
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <Send className="w-4 h-4" />
                      <span>Auto Reply</span>
                      <Switch 
                        checked={integration.auto_reply_enabled} 
                        onCheckedChange={v => handleToggle(integration.integration_id, "auto_reply_enabled", v)} 
                      />
                    </label>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-between items-center pt-3 border-t">
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleOpenSettings(integration)}>
                        <Settings2 className="w-4 h-4 mr-1" />
                        Settings
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleViewLogs(integration)}>
                        <FileText className="w-4 h-4 mr-1" />
                        Logs
                      </Button>
                      {integration.status !== "connected" && (
                        <Button size="sm" variant="outline" onClick={() => handleReconnect(integration.integration_id)}>
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Reconnect
                        </Button>
                      )}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(integration.integration_id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {waIntegrations.length === 0 && (
        <Card className="mt-6">
          <CardContent className="text-center py-12">
            <MessageCircle className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No WhatsApp Connected</h3>
            <p className="text-slate-500 mb-4">{hideConnect ? "Use \"Continue with Facebook\" or add your account details above to connect a number." : "Click the card above to connect your WhatsApp Business API"}</p>
          </CardContent>
        </Card>
      )}

      {/* Connect Dialog */}
      <Dialog open={showConnectDialog} onOpenChange={setShowConnectDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-600" />
              Connect WhatsApp Business API
            </DialogTitle>
            <DialogDescription>
              Connect using the official Meta WhatsApp Business API
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-800">
                <strong>WhatsApp Business API</strong> requires credentials from Meta Business Suite. 
                <a href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started" target="_blank" rel="noopener noreferrer" className="underline ml-1">
                  Learn more <ExternalLink className="w-3 h-3 inline" />
                </a>
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Business Account ID</Label>
                <Input
                  value={apiForm.businessAccountId}
                  onChange={e => setApiForm(prev => ({ ...prev, businessAccountId: e.target.value }))}
                  placeholder="e.g., 123456789012345"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Phone Number ID <span className="text-red-500">*</span></Label>
                <Input
                  value={apiForm.phoneNumberId}
                  onChange={e => setApiForm(prev => ({ ...prev, phoneNumberId: e.target.value }))}
                  placeholder="e.g., 123456789012345"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Access Token <span className="text-red-500">*</span></Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="password"
                  value={apiForm.accessToken}
                  onChange={e => setApiForm(prev => ({ ...prev, accessToken: e.target.value }))}
                  placeholder="Your permanent access token"
                  className="flex-1"
                />
                <Button 
                  variant="outline" 
                  onClick={handleValidateApiToken}
                  disabled={validating}
                >
                  {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Validate"}
                </Button>
              </div>
            </div>

            {validationResult && (
              <div className={`p-3 rounded-lg ${validationResult.valid ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
                {validationResult.valid ? (
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-4 h-4" />
                    <span>Verified: {validationResult.data.verified_name} ({validationResult.data.display_phone_number})</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-700">
                    <XCircle className="w-4 h-4" />
                    <span>{validationResult.error}</span>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Webhook Verify Token</Label>
                <Input
                  value={apiForm.webhookVerifyToken}
                  onChange={e => setApiForm(prev => ({ ...prev, webhookVerifyToken: e.target.value }))}
                  placeholder="Auto-generated if empty"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>App ID</Label>
                <Input
                  value={apiForm.appId}
                  onChange={e => setApiForm(prev => ({ ...prev, appId: e.target.value }))}
                  placeholder="Optional"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>App Secret</Label>
              <Input
                type="password"
                value={apiForm.appSecret}
                onChange={e => setApiForm(prev => ({ ...prev, appSecret: e.target.value }))}
                placeholder="For webhook verification (optional)"
                className="mt-1"
              />
            </div>

            {/* Assignment Rules */}
            <div className="pt-4 border-t">
              <h4 className="font-medium mb-3">Lead Assignment Rules</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Assignment Method</Label>
                  <Select value={assignmentRule} onValueChange={setAssignmentRule}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="round_robin">Round Robin</SelectItem>
                      <SelectItem value="specific_user">Specific User</SelectItem>
                      <SelectItem value="least_busy">Least Busy Rep</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {assignmentRule === "specific_user" && (
                  <div>
                    <Label>Assign To</Label>
                    <Select value={assignToUser} onValueChange={setAssignToUser}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select user" />
                      </SelectTrigger>
                      <SelectContent>
                        {teamMembers.map(member => (
                          <SelectItem key={member.user_id} value={member.user_id}>
                            {member.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Auto Reply Settings */}
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">Auto Reply</h4>
                <Switch 
                  checked={autoReplySettings.enabled} 
                  onCheckedChange={v => setAutoReplySettings(prev => ({ ...prev, enabled: v }))}
                />
              </div>
              {autoReplySettings.enabled && (
                <div className="space-y-3">
                  <div>
                    <Label>Instant Reply Message</Label>
                    <Textarea
                      value={autoReplySettings.instantReply}
                      onChange={e => setAutoReplySettings(prev => ({ ...prev, instantReply: e.target.value }))}
                      placeholder="Message sent immediately when a new chat is received"
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Business Hours Start</Label>
                      <Input
                        type="time"
                        value={autoReplySettings.businessHoursStart}
                        onChange={e => setAutoReplySettings(prev => ({ ...prev, businessHoursStart: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Business Hours End</Label>
                      <Input
                        type="time"
                        value={autoReplySettings.businessHoursEnd}
                        onChange={e => setAutoReplySettings(prev => ({ ...prev, businessHoursEnd: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>After Hours Reply</Label>
                    <Textarea
                      value={autoReplySettings.afterHoursReply}
                      onChange={e => setAutoReplySettings(prev => ({ ...prev, afterHoursReply: e.target.value }))}
                      placeholder="Message sent outside business hours"
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => { setShowConnectDialog(false); resetApiForm(); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleConnectApi} 
              disabled={formLoading || !validationResult?.valid}
              className="bg-green-600 hover:bg-green-700"
            >
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect API
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Integration Settings</DialogTitle>
            <DialogDescription>
              Configure assignment rules and auto-reply for {selectedIntegration?.display_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Assignment Rules */}
            <div>
              <h4 className="font-medium mb-3">Lead Assignment</h4>
              <div className="space-y-3">
                <div>
                  <Label>Assignment Method</Label>
                  <Select value={assignmentRule} onValueChange={setAssignmentRule}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="round_robin">Round Robin</SelectItem>
                      <SelectItem value="specific_user">Specific User</SelectItem>
                      <SelectItem value="least_busy">Least Busy Rep</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {assignmentRule === "specific_user" && (
                  <div>
                    <Label>Assign To</Label>
                    <Select value={assignToUser} onValueChange={setAssignToUser}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select user" />
                      </SelectTrigger>
                      <SelectContent>
                        {teamMembers.map(member => (
                          <SelectItem key={member.user_id} value={member.user_id}>
                            {member.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Auto Reply */}
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">Auto Reply</h4>
                <Switch 
                  checked={autoReplySettings.enabled} 
                  onCheckedChange={v => setAutoReplySettings(prev => ({ ...prev, enabled: v }))}
                />
              </div>
              {autoReplySettings.enabled && (
                <div className="space-y-3">
                  <div>
                    <Label>Instant Reply</Label>
                    <Textarea
                      value={autoReplySettings.instantReply}
                      onChange={e => setAutoReplySettings(prev => ({ ...prev, instantReply: e.target.value }))}
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Business Hours Start</Label>
                      <Input
                        type="time"
                        value={autoReplySettings.businessHoursStart}
                        onChange={e => setAutoReplySettings(prev => ({ ...prev, businessHoursStart: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Business Hours End</Label>
                      <Input
                        type="time"
                        value={autoReplySettings.businessHoursEnd}
                        onChange={e => setAutoReplySettings(prev => ({ ...prev, businessHoursEnd: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>After Hours Reply</Label>
                    <Textarea
                      value={autoReplySettings.afterHoursReply}
                      onChange={e => setAutoReplySettings(prev => ({ ...prev, afterHoursReply: e.target.value }))}
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSettings}>
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logs Dialog */}
      <Dialog open={showLogsDialog} onOpenChange={setShowLogsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Integration Logs</DialogTitle>
            <DialogDescription>
              Recent activity for {selectedIntegration?.display_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-96 overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-center py-8 text-slate-500">No logs available</p>
            ) : (
              <div className="space-y-2">
                {logs.map((log, i) => (
                  <div key={i} className="p-3 bg-slate-50 rounded-lg text-sm">
                    <div className="flex justify-between items-start">
                      <span className={`font-medium ${log.level === "error" ? "text-red-600" : log.level === "warning" ? "text-amber-600" : "text-slate-700"}`}>
                        {log.event || log.message}
                      </span>
                      <span className="text-xs text-slate-400">
                        {new Date(log.created_at).toLocaleString()}
                      </span>
                    </div>
                    {log.details && (
                      <p className="text-slate-500 mt-1 text-xs">{JSON.stringify(log.details)}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
